/**
 * 来源分析需要明显的时间筛选入口；多个分公司共用同一已应用区间。
 * 自定义日期填写完再应用，避免半填日期时把旧图表误当成新结果；所有快捷日期按加州日历计算。
 */
import React, { useEffect, useState } from "react";
import { CalendarRange } from "lucide-react";
import { laToday } from "../tm/tmConst";

export const ALL_MARKETING_DATES = { preset: "all", start: "", end: "" };

/** 根据日历日生成包含首尾日期的快捷区间，UTC 运算只用于日期加减，不改变业务时区。 */
export function marketingPreset(preset, today = laToday()) {
  const [year, month] = today.split("-").map(Number);
  const day = (y, m, d) => new Date(Date.UTC(y, m - 1, d)).toISOString().slice(0, 10);
  if (preset === "thisMonth") return { preset, start: day(year, month, 1), end: day(year, month + 1, 0) };
  if (preset === "lastMonth") return { preset, start: day(year, month - 1, 1), end: day(year, month, 0) };
  if (preset === "last30") return { preset, start: day(year, month, Number(today.slice(8)) - 29), end: today };
  if (preset === "thisYear") return { preset, start: `${year}-01-01`, end: `${year}-12-31` };
  return { ...ALL_MARKETING_DATES };
}

export function marketingPeriodLabel(value, zh) {
  return value.start && value.end ? `${value.start} — ${value.end}` : zh ? "全部时间" : "All time";
}

/** 草稿在控件内保存；onChange 只提交完整有效范围，同一分公司的图表、表格及导出统一更新。 */
export function MarketingPeriodFilter({ value, onChange, zh, id }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const valid = draft.start && draft.end && draft.start <= draft.end;
  const cls = "min-h-11 md:min-h-9 min-w-0 rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-400";
  return (
    <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50/70 p-3" data-testid={`${id}-filter`}>
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-xs font-medium text-slate-600" htmlFor={`${id}-preset`}>
          <span className="flex items-center gap-1"><CalendarRange className="h-3.5 w-3.5" aria-hidden="true" />{zh ? "时间范围" : "Date range"}</span>
          <select id={`${id}-preset`} data-testid={`${id}-preset`} className={cls} value={draft.preset} onChange={e => {
            if (e.target.value === "custom") setDraft({ ...draft, preset: "custom" });
            else onChange(marketingPreset(e.target.value));
          }}>
            {[["all", "全部时间", "All time"], ["thisMonth", "本月", "This month"], ["lastMonth", "上月", "Last month"],
              ["last30", "近 30 天", "Last 30 days"], ["thisYear", "今年", "This year"], ["custom", "自定义时间段", "Custom dates"]]
              .map(([key, cn, en]) => <option key={key} value={key}>{zh ? cn : en}</option>)}
          </select>
        </label>
        {draft.preset === "custom" && <>
          <div className="grid w-full grid-cols-2 gap-2 sm:w-auto">
            <label className="flex min-w-0 flex-col gap-1 text-xs text-slate-600" htmlFor={`${id}-start`}>
              {zh ? "开始日期" : "Start date"}
              <input id={`${id}-start`} data-testid={`${id}-start`} type="date" className={`${cls} w-full`} value={draft.start} max={draft.end || undefined} onChange={e => setDraft({ ...draft, start: e.target.value })} />
            </label>
            <label className="flex min-w-0 flex-col gap-1 text-xs text-slate-600" htmlFor={`${id}-end`}>
              {zh ? "结束日期" : "End date"}
              <input id={`${id}-end`} data-testid={`${id}-end`} type="date" className={`${cls} w-full`} value={draft.end} min={draft.start || undefined} onChange={e => setDraft({ ...draft, end: e.target.value })} />
            </label>
          </div>
          <button type="button" data-testid={`${id}-apply`} disabled={!valid} onClick={() => onChange({ ...draft })}
            className="min-h-11 md:min-h-9 rounded-lg bg-indigo-600 px-4 text-sm font-medium text-white disabled:opacity-40 hover:bg-indigo-700">{zh ? "应用筛选" : "Apply"}</button>
        </>}
        <span className="py-2 text-xs font-medium text-indigo-700" data-testid={`${id}-applied`}>
          {zh ? "已应用：" : "Applied: "}{marketingPeriodLabel(value, zh)}
        </span>
      </div>
      {draft.preset === "custom" && draft.start && draft.end && !valid &&
        <p role="alert" className="mt-2 text-xs text-red-600">{zh ? "结束日期不能早于开始日期" : "End date must not precede start date"}</p>}
      <p className="mt-2 text-xs leading-relaxed text-slate-500">
        {zh ? "按客户录入日期筛选，包含起止当天；有效／签约为这些客户的当前状态。" : "Filter by lead creation date, inclusive. Qualified / signed reflect these leads’ current status."}
      </p>
    </div>
  );
}
